import React, { useState } from 'react';
import axios from 'axios';
import config from '../../config';
import CadreEtape, { champ, etiquette } from './CadreEtape';
import { boutonPrincipal } from './ParcoursInitial';

const API_URL = config.API_URL;

/**
 * Troisième étape : créer le site.
 *
 * ## Ce qu'un site sert à faire, et qu'il faut dire ici
 *
 * Le site est ce qui **réunit** les comptes d'un même périmètre — la régie et la solution de
 * mesure. C'est lui, et lui seul, qui permet au dashboard consolidé de croiser une dépense
 * publicitaire avec un chiffre d'affaires analytique. Sans site, les dashboards par régie
 * fonctionnent, mais les colonnes Budget, CPA et COS restent vides sans que rien ne l'explique.
 *
 * ## Le type de site est exigé ici, alors qu'il est facultatif ailleurs
 *
 * Dans la modale du Gestionnaire (`kanban/modals/CreateSiteModal.jsx`), `null` est un état
 * légitime : on y crée un site sans savoir encore ce qu'on en fera. Ici, la question est posée
 * au bon moment — avant même qu'un compte existe — et sa réponse décide du modèle du tableau de
 * bord consolidé : chiffre d'affaires et panier moyen d'un côté, conversions et coût par
 * acquisition de l'autre. La laisser facultative, c'est garantir qu'elle ne sera pas renseignée
 * et que tout le monde recevra le modèle e-commerce par défaut, y compris les sites à
 * formulaires.
 *
 * ⚠️ Déclarer `LEAD` ne suffit pas à lui seul : le modèle est porté par la **propriété de
 * mesure** (`LibraryItem.modeleConsolide`), et seul Google Analytics sait compter un événement
 * de conversion nommé. `services/modeleDuSite.js` fait le report quand une propriété GA4 est
 * rattachée à un site de ce type.
 *
 * Le site est rattaché au client créé à l'étape précédente. Le serveur ne vérifie pas que le
 * `clientId` appartient bien au demandeur : il vient de notre propre lecture d'état, pas d'une
 * saisie.
 */
function EtapeSite({ token, etat, onAvance }) {
  const [nom, setNom] = useState('');
  const [url, setUrl] = useState('');
  const [typeSite, setTypeSite] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const creer = async (e) => {
    e.preventDefault();
    const propre = nom.trim();
    if (!propre || !typeSite) return;

    setEnvoi(true);
    setErreur(null);
    try {
      // Même contrainte d'unicité que pour le client, et même absence de pré-vérification
      // côté serveur : on nomme le doublon plutôt que de laisser tomber une erreur sans motif.
      const { data } = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const existants = data?.sites || [];
      if (existants.some((s) => (s.name || '').toLowerCase() === propre.toLowerCase())) {
        setErreur(`« ${propre} » existe déjà. Choisissez un autre nom.`);
        setEnvoi(false);
        return;
      }

      await axios.post(`${API_URL}/api/sites`, {
        name: propre,
        url: url.trim(),
        typeSite,
        clientId: etat?.client?.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      await onAvance();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Impossible de créer le site');
      setEnvoi(false);
    }
  };

  const boutonType = (valeur, libelle) => (
    <button
      type="button"
      onClick={() => setTypeSite(valeur)}
      style={{
        flex: 1, padding: '10px', borderRadius: '8px', fontSize: '14px',
        border: `1px solid ${typeSite === valeur ? '#1a1a1a' : '#d1d5db'}`,
        background: typeSite === valeur ? '#1a1a1a' : 'white',
        color: typeSite === valeur ? 'white' : '#374151',
        cursor: 'pointer'
      }}
    >
      {libelle}
    </button>
  );

  return (
    <CadreEtape
      titre="Votre premier site"
      explication="Un site réunit les comptes d’un même périmètre — la régie, la solution de mesure. C’est lui qui permet de croiser dépenses et résultats."
      erreur={erreur}
    >
      <form onSubmit={creer}>
        <label style={etiquette} htmlFor="nom-site">Nom du site</label>
        <input
          id="nom-site"
          value={nom}
          onChange={(e) => { setNom(e.target.value); setErreur(null); }}
          placeholder="Mon site"
          autoFocus
          style={champ}
        />

        <label style={{ ...etiquette, marginTop: '16px' }} htmlFor="url-site">
          Adresse du site <span style={{ color: '#9ca3af', fontWeight: 400 }}>(facultatif)</span>
        </label>
        <input
          id="url-site"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="exemple.fr"
          style={champ}
        />

        <label style={{ ...etiquette, marginTop: '16px' }}>Type de site</label>
        <div style={{ display: 'flex', gap: '10px' }}>
          {boutonType('ECOMMERCE', 'E-commerce')}
          {boutonType('LEAD', 'Lead')}
        </div>
        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0' }}>
          Il décide du modèle de votre tableau de bord : chiffre d’affaires et panier moyen pour
          un site e-commerce, conversions et coût par acquisition pour un site à formulaires.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '22px' }}>
          <button
            type="submit"
            disabled={!nom.trim() || !typeSite || envoi}
            style={{
              ...boutonPrincipal,
              cursor: !nom.trim() || !typeSite || envoi ? 'not-allowed' : 'pointer',
              opacity: !nom.trim() || !typeSite || envoi ? 0.5 : 1
            }}
          >
            {envoi ? 'Création…' : 'Continuer'}
          </button>
        </div>
      </form>
    </CadreEtape>
  );
}

export default EtapeSite;
